"use client";

import type { JSONContent } from "@tiptap/react";

import type { ReactNode, RefObject } from "react";

import {
  type EmailAttachmentFile,
  EmailComposer,
  type EmailComposerRef,
} from "@/components/email-composer";
import { Button } from "@/components/ui";

import { cx } from "@/lib/tailwind-utils";

import type { EmailTemplate } from "@/types/email-template";
import type { SuggestionItem } from "@/types/suggestions";

import { EmailThemeToggle, useEmailThemeToggle } from "./email-theme-toggle";

/**
 * Editing surface for inline thread composers (reply / forward / compose):
 * transparent, full-bleed, and unpadded so the generated bodyHtml is plain
 * left-aligned content like a Gmail reply — not the marketing composer's
 * centered, padded 600px card. These settings drive both the editor canvas and
 * the sent HTML (they share `styleSettings`), so it stays WYSIWYG. The blast
 * composer keeps the default centered, padded container.
 */
const TRANSPARENT_CONTAINER = {
  containerBackgroundColor: "transparent",
  maxWidth: 9999,
  containerPaddingTop: 0,
  containerPaddingBottom: 0,
  containerPaddingLeft: 0,
  containerPaddingRight: 0,
};

/**
 * Flattens the shared FloatingToolbar from a floating pill into a plain,
 * borderless icon row that sits inline above the writing area. Centered
 * (justify-center) so the icon cluster reads as intentional rather than
 * clinging to the left edge with dead space beside it.
 */
const FLAT_TOOLBAR =
  "static mx-0 mb-2 w-full justify-center rounded-none border-t-0 bg-transparent px-0 py-1 shadow-none backdrop-blur-none";

interface ThreadComposerEditorProps {
  composerRef: RefObject<EmailComposerRef | null>;
  composerId: string;
  placeholder: string;
  attachments: EmailAttachmentFile[];
  onAddFiles: (files: File[]) => { added: number; errors: string[] };
  onRemoveAttachment: (id: string) => void;
  /**
   * `EmailComposer.AttachmentChip`s listed before the files attached here. Forward
   * passes the forwarded email's own attachments; the surface owns their removal.
   */
  attachmentChips?: ReactNode;
  initialContent?: JSONContent;
  variables?: SuggestionItem[];
  autoFocus?: boolean;
  /** Canvas min-height / sizing override (defaults to a compact reply height). */
  canvasClassName?: string;
  /** Extra classes appended to the composer wrapper. */
  className?: string;
  /** Notified on every editor edit — used to drive draft auto-save. */
  onChange?: (json: JSONContent) => void;
  /**
   * Called after a template is applied, for surfaces that own an editable
   * subject. Reply and forward omit it: their subject is a derived Re:/Fwd:
   * line that keeps the thread stitched together, so a template must leave it
   * alone. Compose owns a real subject field and gates Send on it, so it opts
   * in and fills it — the same way every other compose surface does.
   */
  onTemplateSelect?: (template: EmailTemplate) => void;
  /**
   * Extra controls for the template row, rendered beside the picker and inside
   * the composer's provider — so a caller can drop in `EmailComposer` compound
   * slots (e.g. `<EmailComposer.SaveAsTemplate />`) without threading a ref.
   *
   * A slot rather than a flag for the same reason `recipients`/`footer` are:
   * which controls belong here is the surface's call, not a mode this
   * component switches on. Compose passes the save action; reply and forward
   * pass nothing, because they carry an `emailQuote` of the thread they answer
   * that would be saved into the template body. Invert `collectQuoteRows`
   * (load-template.tsx) and they can pass it too.
   */
  templateActions?: ReactNode;
}

/**
 * The shared transparent, flat-toolbar email editor used by every email-thread
 * composer (reply, forward, and the compose drawer). Centralizes the
 * transparent surface + flattened toolbar so callers never re-wire the engine.
 */
export function ThreadComposerEditor({
  composerRef,
  composerId,
  placeholder,
  attachments,
  onAddFiles,
  onRemoveAttachment,
  attachmentChips,
  initialContent,
  variables,
  autoFocus,
  canvasClassName = "min-h-28",
  className,
  onChange,
  onTemplateSelect,
  templateActions,
}: ThreadComposerEditorProps) {
  const { mode, isOverridden, toggle } = useEmailThemeToggle();

  // Preview surface for the writing area. Untouched (no override) it stays
  // app-blended/transparent, so it matches the composer's own surface exactly
  // (including the compose drawer's translucent glass). An explicit flip paints
  // the *opposite* theme's surface as a distinct "paper" — the toggle's cycle
  // only ever reaches the opposite of the app theme, so from a dark app you get
  // white paper (how the email is actually sent, so designed templates read
  // right), and from a light app you get a dark surface. Display-only: applied
  // as CSS on the live editor, never through styleSettings, so the sent HTML is
  // byte-identical. The colors are intentionally theme-INDEPENDENT literals, not
  // bg-background/text-foreground: those track the *current* app theme, but this
  // previews a fixed surface — white paper is what every email is actually sent
  // on (#fff / #1a1a1a === EMAIL_TOKENS.colors.text.primary), and the dark
  // preview must stay dark even from a light app (#1b211b / #ebe4da === the
  // default-dark theme's background/foreground). Same rationale as the reader
  // and EMAIL_TOKENS, where email surface colors are deliberately hardcoded.
  //
  // Known limitation (dark preview only): this is a best-effort surface swap —
  // it recolors the inherited .ProseMirror text but NOT template blocks that
  // carry their own inline color (a heading via styleSettings.headingColor
  // defaulting to #1a1a1a, a TextColor-marked span). Those keep their color and
  // can read dark-on-dark. Accepted rather than fixed: faithful dark rendering
  // would need a full color invert (as the reader does) or flattening the
  // template's intentional colors — both worse for a design preview. The reader
  // is unaffected; its dark mode inverts.
  const surfaceClass = !isOverridden
    ? "[&_.ProseMirror]:text-foreground"
    : mode === "light"
      ? "[&_.email-editor]:!bg-white [&_.ProseMirror]:!text-[#1a1a1a]"
      : "[&_.email-editor]:!bg-[#1b211b] [&_.ProseMirror]:!text-[#ebe4da]";

  return (
    <EmailComposer
      ref={composerRef}
      composerId={composerId}
      initialContent={initialContent}
      variables={variables}
      styleSettings={TRANSPARENT_CONTAINER}
      onAddFiles={onAddFiles}
      onChange={onChange}
      placeholder={placeholder}
      resolveOnInsert
      className={cx(surfaceClass, className)}
    >
      {/* Template controls (LoadTemplate self-hides when no templates exist; a
          template replaces the draft but keeps the quoted original below it). */}
      <div className="flex items-center justify-end gap-1">
        <EmailComposer.LoadTemplate onSelect={onTemplateSelect} />
        {templateActions}
      </div>
      {/* The light/dark preview toggle rides in the toolbar as one more icon
          button (past a divider), rather than a standalone pill above it. */}
      <EmailComposer.FloatingToolbar className={FLAT_TOOLBAR}>
        <EmailThemeToggle
          mode={mode}
          onToggle={toggle}
          className={cx(
            "shrink-0 text-foreground-muted",
            isOverridden && "text-primary",
          )}
        />
      </EmailComposer.FloatingToolbar>
      <EmailComposer.Canvas autoFocus={autoFocus} className={canvasClassName} />
      <EmailComposer.LinkBubbleMenu />
      <EmailComposer.ImageBubbleMenu />
      <EmailComposer.Attachments
        attachments={attachments}
        onRemove={onRemoveAttachment}
      >
        {attachmentChips}
      </EmailComposer.Attachments>
    </EmailComposer>
  );
}

/** The "Cc/Bcc" reveal control shared across all email-thread composers. */
export function CcBccToggle({
  onClick,
  className,
}: {
  onClick: () => void;
  className?: string;
}) {
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={onClick}
      className={cx("shrink-0 text-caption text-foreground-muted", className)}
    >
      Cc/Bcc
    </Button>
  );
}

interface InlineThreadComposerProps {
  /** Recipient fields block (To + optional Cc/Bcc, with the Cc/Bcc toggle). */
  recipients?: ReactNode;
  /** Footer actions (Send / Forward + Cancel). */
  footer: ReactNode;
  /** The editor — typically <ThreadComposerEditor />. */
  children: ReactNode;
}

/**
 * Flat, borderless chrome for an inline thread composer (reply / forward).
 * Renders directly inside the expanded message card — no nested card, no title
 * label — just recipients, the editor, and a footer separated by spacing. The
 * To field + toolbar + Send already make it self-evidently a composer.
 */
export function InlineThreadComposer({
  recipients,
  footer,
  children,
}: InlineThreadComposerProps) {
  return (
    <div>
      {recipients}
      {children}
      <div className="mt-3 flex items-center justify-end gap-2">{footer}</div>
    </div>
  );
}
